import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft, Tv, Truck, Radio } from "lucide-react";

function QRCard({ label, sub, url }) {
  const [qr, setQr] = useState("");
  useEffect(() => { if (url) QRCode.toDataURL(url, { width: 340, margin: 1 }).then(setQr).catch(() => {}); }, [url]);
  return (
    <div data-testid="install-qr-card" className="flex break-inside-avoid flex-col items-center gap-2 rounded-xl border border-slate-300 p-4 text-center">
      {qr ? <img src={qr} alt="QR" className="h-40 w-40" /> : <div className="h-40 w-40" />}
      <div className="text-base font-bold text-slate-900">{label}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
      <div className="break-all font-mono text-[9px] leading-tight text-slate-400">{url}</div>
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="mb-8 break-inside-avoid">
      <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-2">
        <Icon className="h-5 w-5 text-slate-700" />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <span className="text-sm text-slate-400">({count})</span>
      </div>
      {count === 0 ? <div className="text-sm text-slate-400">Geen gekoppelde apparaten.</div>
        : <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{children}</div>}
    </div>
  );
}

export default function InstallOverview() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [monitors, setMonitors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [pagers, setPagers] = useState([]);
  const origin = window.location.origin;

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/login"); return; }
    api.get("/monitor/devices").then(({ data }) => setMonitors((data || []).filter((m) => m.status === "approved" && m.token))).catch(() => {});
    api.get("/devices").then(({ data }) => setDevices((data || []).filter((d) => d.status === "approved" && d.token))).catch(() => {});
    api.get("/pager/devices").then(({ data }) => setPagers((data || []).filter((p) => p.status === "approved" && p.token))).catch(() => {});
  }, [user, ready, navigate]);

  return (
    <div className="min-h-screen bg-white p-8 text-slate-900" data-testid="install-overview-page">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <button data-testid="install-back" onClick={() => navigate("/beheer")} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /> Terug naar Beheer</button>
          <button data-testid="install-print" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>
        <div className="mb-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">Installatie-overzicht</h1>
          <p className="text-sm text-slate-500">Scan een QR met het betreffende toestel om de app te installeren en automatisch in te loggen. Voeg daarna toe aan het beginscherm voor een app-icoon.</p>
        </div>

        <Section icon={Tv} title="Kazernemonitors" count={monitors.length}>
          {monitors.map((m) => (
            <QRCard key={m.id} label={m.label || "Kazernemonitor"} sub={m.location_name || "Geen locatie"} url={`${origin}/kazerne?token=${m.token}`} />
          ))}
        </Section>

        <Section icon={Truck} title="Voertuig-tablets" count={devices.length}>
          {devices.map((d) => (
            <QRCard key={d.id} label={d.vehicle ? `${d.vehicle.callsign}` : (d.label || "Tablet")} sub={[d.label, d.vehicle?.name].filter(Boolean).join(" · ")} url={`${origin}/voertuig?token=${d.token}`} />
          ))}
        </Section>

        <Section icon={Radio} title="Pagers" count={pagers.length}>
          {pagers.map((p) => (
            <QRCard key={p.id} label={p.label || "Pager"} sub={p.location_name || "Persoonspager"} url={`${origin}/pager?token=${p.token}`} />
          ))}
        </Section>
      </div>
    </div>
  );
}
